import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { 
  Target, Users, Play, ArrowRight, Crown, MessageCircle, 
  Sparkles, Eye, Heart
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import UniversalHeartToggle from './UniversalHeartToggle';

const MultiPathLandingPage = ({ 
  onNavigateToSpiralCouncil, 
  onNavigateToProjects, 
  onNavigateToGameplay,
  onNavigateToAuth 
}) => {
  const [spiralPhase, setSpiralPhase] = useState(0);

  // Grant spiral animation
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.2) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={{ 
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_planetaryfed-v3/artifacts/ugnfkky4_ChatGPT%20Image%20Sep%2011%2C%202025%20at%2012_35_28%20AM.jpeg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Grant spiral background */}
      <div className="fixed inset-0 pointer-events-none opacity-25" style={{ zIndex: 1 }}>
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(8) rotate(${spiralPhase}deg)`,
            filter: 'blur(3px) brightness(1.4) saturate(1.6)',
            mixBlendMode: 'overlay'
          }}
        >
          <GrantitudeSpiral size={300} />
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-transparent to-black/60"></div>

      <div className="relative z-10 flex flex-col min-h-screen">
        
        {/* Header */}
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="text-center max-w-4xl">
            
            {/* Main Title */}
            <h1 
              className="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-wider mb-8"
              style={{
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 50px rgba(210, 180, 140, 1.2), 0 0 100px rgba(222, 184, 135, 0.9)',
                letterSpacing: '0.08em',
                color: '#E6C794',
                filter: 'brightness(1.3)'
              }}
            >
              <span style={{ fontSize: '0.65em' }}>THE</span> <span style={{ fontSize: '1.1em' }}>SECRET HISTORY</span> <span style={{ fontSize: '0.65em' }}>OF</span> <span style={{ fontSize: '1.1em' }}>HUMANKIND</span>
            </h1>
            
            {/* Subtitle */}
            <p 
              className="text-lg md:text-xl font-bold tracking-widest mb-12"
              style={{
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 25px rgba(210, 180, 140, 1.1)',
                letterSpacing: '0.2em',
                color: '#E6C794',
                filter: 'brightness(1.3)'
              }}
            >
              PLAYED IN REAL TIME
            </p>

            {/* Three Path Options */}
            <div className="space-y-6">
              <p 
                className="text-base md:text-lg text-cyan-200 mb-8"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                Choose Your Path:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                
                {/* Remedy Council Path */}
                <button
                  onClick={onNavigateToSpiralCouncil}
                  className="group bg-purple-900/40 backdrop-blur-xl border border-purple-400/30 rounded-2xl p-6 hover:bg-purple-900/60 transition-all duration-300"
                >
                  <div className="text-center space-y-4">
                    <div className="w-16 h-16 mx-auto bg-purple-500/30 rounded-full flex items-center justify-center group-hover:bg-purple-500/50 transition-all">
                      <Target className="w-8 h-8 text-purple-300" />
                    </div>
                    <h3 className="text-xl font-bold text-purple-200">🌀 REMEDY COUNCIL</h3>
                    <p className="text-purple-300 text-sm">
                      Conflict Resolution & Wisdom<br/>
                      Sacred space for breakthrough solutions
                    </p>
                    <ArrowRight className="w-5 h-5 text-purple-400 mx-auto group-hover:translate-x-1 transition-all" />
                  </div>
                </button>

                {/* Projects Path */}
                <button
                  onClick={onNavigateToProjects}
                  className="group bg-cyan-900/40 backdrop-blur-xl border border-cyan-400/30 rounded-2xl p-6 hover:bg-cyan-900/60 transition-all duration-300"
                >
                  <div className="text-center space-y-4">
                    <div className="w-16 h-16 mx-auto bg-cyan-500/30 rounded-full flex items-center justify-center group-hover:bg-cyan-500/50 transition-all">
                      <Users className="w-8 h-8 text-cyan-300" />
                    </div>
                    <h3 className="text-xl font-bold text-cyan-200">🎯 MY PROJECTS</h3>
                    <p className="text-cyan-300 text-sm">
                      Active Collaborations & Initiatives<br/>
                      Your reality creation workspace
                    </p>
                    <ArrowRight className="w-5 h-5 text-cyan-400 mx-auto group-hover:translate-x-1 transition-all" />
                  </div>
                </button>

                {/* Gameplay Path */}
                <button
                  onClick={onNavigateToGameplay}
                  className="group bg-yellow-900/40 backdrop-blur-xl border border-yellow-400/30 rounded-2xl p-6 hover:bg-yellow-900/60 transition-all duration-300"
                >
                  <div className="text-center space-y-4">
                    <div className="w-16 h-16 mx-auto bg-yellow-500/30 rounded-full flex items-center justify-center group-hover:bg-yellow-500/50 transition-all">
                      <Play className="w-8 h-8 text-yellow-300" />
                    </div>
                    <h3 className="text-xl font-bold text-yellow-200">🎮 CONSCIOUSNESS GAME</h3>
                    <p className="text-yellow-300 text-sm">
                      Complete Experience<br/>
                      Epic consciousness expansion journey
                    </p>
                    <ArrowRight className="w-5 h-5 text-yellow-400 mx-auto group-hover:translate-x-1 transition-all" />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom branding */}
        <div className="absolute bottom-4 right-4 z-20">
          <p 
            className="text-xs text-cyan-200/70"
            style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
          >
            brought to you by Planetary Fed
          </p>
        </div>

        {/* Universal Heart Toggle */}
        <div className="absolute bottom-4 left-4 z-20">
          <UniversalHeartToggle />
        </div>
      </div>
    </div>
  );
};

export default MultiPathLandingPage;